import React from 'react';
import { Link } from 'gatsby';
import { Link as ScrollLink, animateScroll as scroll } from 'react-scroll';
import { useStaticQuery, graphql } from 'gatsby';
import Img from 'gatsby-image';
import BackgroundImg from 'gatsby-background-image';
import { LocalizedLink } from '../components/language/localized-link';
import { WithIntl } from '../components/language/with-intl.js';
import Layout from '../components/layout';
import SEO from '../components/seo';
import { useTranslation } from 'react-i18next';
import BlogShowcase from '../pages/blog-showcase';
import Testimonials from '../components/testimonials';

const Home = ({ language }) => {
    const data = useStaticQuery(graphql`
        query {
            image: file(relativePath: { eq: "home.jpeg" }) {
                childImageSharp {
                    fluid(quality: 90) {
                        ...GatsbyImageSharpFluid
                    }
                }
            }
            en: allMarkdownRemark(filter: { fields: { langKey: { eq: "en" } } }, sort: { fields: [frontmatter___date], order: DESC }, limit: 3) {
                edges {
                    node {
                        fields {
                            slug
                            langKey
                        }
                        timeToRead
                        excerpt(pruneLength: 100)
                        frontmatter {
                            date(formatString: "MMMM DD, YYYY")
                            title
                            author
                            authorImage {
                                childImageSharp {
                                    fixed(height: 36, width: 36, quality: 99) {
                                        ...GatsbyImageSharpFixed
                                    }
                                }
                            }
                            featuredImage {
                                childImageSharp {
                                    fluid(quality: 95) {
                                        ...GatsbyImageSharpFluid
                                    }
                                }
                            }
                        }
                    }
                }
            }
            gr: allMarkdownRemark(filter: { fields: { langKey: { eq: "gr" } } }, sort: { fields: [frontmatter___date], order: DESC }, limit: 3) {
                edges {
                    node {
                        fields {
                            slug
                            langKey
                        }
                        timeToRead
                        excerpt(pruneLength: 100)
                        frontmatter {
                            date(formatString: "MMMM DD, YYYY")
                            title
                            author
                            authorImage {
                                childImageSharp {
                                    fixed(height: 36, width: 36, quality: 99) {
                                        ...GatsbyImageSharpFixed
                                    }
                                }
                            }
                            featuredImage {
                                childImageSharp {
                                    fluid(quality: 95) {
                                        ...GatsbyImageSharpFluid
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    `);
    const { t } = useTranslation();

    return (
        <Layout>
            <SEO title={t('home.seo_title')} description={t('home.seo_description')} />
            <section className="mt-10 mb-10 mb-md-6 md-lg-10 mb-xl-0" id="home">
                <div className="container col-12 header">
                    <div className="row align-items-stretch">
                        <div className="col-12 col-md-12 col-lg-6 col-xl-4 header-image order-lg-2 overflow-lg-hidden">
                            <div className="position-relative d-none d-lg-block mr-n30">
                                <div className="shape svg-shim vertical-shape">
                                    <svg height="1150" width="300" viewBox="0 0 251.73334 800" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M 0,0 V 399.30524 800 h 251.73334 c -465.42223,-400.69476 0,-800 0,-800 z" fill="white" />
                                    </svg>
                                </div>
                                <BackgroundImg fluid={data.image.childImageSharp.fluid} Tag="div" className="w-100 h-100 bg-cover">
                                    <div className="background-image" />
                                </BackgroundImg>
                            </div>
                            <div className="d-lg-none img-cover">
                                <div className="w-100">
                                    <Img fluid={data.image.childImageSharp.fluid} />
                                </div>
                            </div>
                        </div>
                        <div className="col-12 col-md-12 col-lg-6 col-xl-5 offset-xl-1 py-8 order-lg-1">
                            <div className="row">
                                <div className="col-12 col-md-12 col-lg-11 text-md-center text-lg-left">
                                    <h1 className="font-size-home-header font-weight-bold" data-aos="fade-right">
                                        {t('home.title_pt1')} <span className="text-primary">{t('home.title_pt2')}</span> {t('home.title_pt3')}{' '}
                                        <span className="text-primary">{t('home.title_pt4')}</span>
                                    </h1>

                                    <div className={language === 'gr' ? 'row mt-md-6 mt-lg-5' : 'row mt-md-6 mt-lg-10'}>
                                        <div className="col-12 col-md-10 offset-md-1 offset-lg-0 col-lg-11" data-aos="fade-right" data-aos-delay="200">
                                            <p className="lead text-gray-800">
                                                {t('home.subheading_pt1')} <strong>Think Tank</strong>, {t('home.subheading_pt2')}
                                            </p>
                                        </div>
                                    </div>
                                    <form
                                        className="form-row justify-content-center mt-md-6 mt-lg-8"
                                        name="email"
                                        method="POST"
                                        action="/subscriber"
                                        data-netlify="true"
                                        data-aos="fade-right"
                                        data-aos-delay="300"
                                    >
                                        <label htmlFor="email-input-hidden" type="hidden" />
                                        <input id="email-input-hidden" type="hidden" name="form-name" value="email" />
                                        <label htmlFor="email-input" className="col-md-12 ml-2 text-muted mt-n5 font-weight-bold">
                                            {t('email_input')}
                                        </label>
                                        <input
                                            id="email-input"
                                            name="email"
                                            type="email"
                                            className="custom-input-primary form-control shadow shadow-lg col-md-7 col-12 mb-5 mb-md-0 mr-md-5"
                                            placeholder="Email"
                                            required
                                        />
                                        <button type="submit" className="btn btn-primary shadow shadow-lg lift col-12 col-md-4">
                                            {t('cta')}
                                        </button>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="position-relative py-9 py-md-13 py-lg-10 mt-xl-13 d-none d-md-block" style={{ zIndex: 1 }}>
                    <div className="shape shape-bottom shape-fluid-x svg-shim text-gray-200">
                        <svg viewBox="0 0 2880 690" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M0 345H1437.5H2880V0C1437.5 462.106 0 0 0 0V345Z" fill="#336699" />
                        </svg>
                    </div>
                </div>
            </section>
            <div className="container col-12 mt-n30 pb-lg-10" style={{ zindex: 4 }} data-aos="fade-up">
                <div className="row align-items-stretch">
                    <div className="col-12 col-lg-7 offset-xl-1 col-xl-5">
                        <div className="row">
                            <div className="col-12 col-lg-11">
                                <div className="row">
                                    <div className="col-12">
                                        <LocalizedLink className="card card-project-header stacked--up text-decoration-none" to="/projects/uniform">
                                            <div className="card-body text-center text-sm-left">
                                                <div className="card-project-title">
                                                    Project <span className="text-primary">Uniform</span>
                                                </div>
                                                <div className="card-text">{t('uniform_description')}</div>
                                            </div>
                                        </LocalizedLink>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="position-relative py-9 py-lg-10 d-md-none" style={{ zIndex: 1 }}>
                <div className="shape shape-bottom shape-fluid-x svg-shim text-gray-200">
                    <svg viewBox="0 0 2880 690" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0 345H1437.5H2880V0C1437.5 462.106 0 0 0 0V345Z" fill="#336699" />
                    </svg>
                </div>
            </div>
            <section className="bg-secondary mt-lg-n10 mt-xl-n5">
                <div className="container col-12">
                    <div className="row align-items-stretch pt-lg-12 pt-md-15">
                        <div className="col-12 col-md-8 offset-md-2 col-xl-6 offset-xl-3 text-center">
                            <h2 className="display-2 text-white font-weight-bold">{t('home.together.heading')}</h2>
                            <p className="lead text-white">{t('home.together.subheading')}</p>
                        </div>
                    </div>
                    <div className="row mt-md-10">
                        <div className="col-12 col-md-10 offset-md-1">
                            <div className="row">
                                {typeof data !== 'undefined' ? (
                                    <BlogShowcase data={language === 'en' ? data.en : data.gr} />
                                ) : (
                                    <div className="text-center mt-10">
                                        <div className="lds-ring">
                                            <div></div>
                                            <div></div>
                                            <div></div>
                                            <div></div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="row justify-content-center mb-5 mt-5 pb-12 pb-xl-14">
                                <div className="col-10 col-md-8 col-lg-6 col-xl-5">
                                    <LocalizedLink to="/blog" className="btn btn-block btn-outline-gray-300 text-white d-flex align-items-center">
                                        <span className="mx-auto">{t('home.together.cta')}</span>
                                        <svg width="24px" height="24px" viewBox="0 0 24 24" version="1.1" xmlns="http://www.w3.org/2000/svg">
                                            <g id="Stockholm-icons-/-Navigation-/-Arrow-right" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                                                <polygon id="Shape" points="0 0 24 0 24 24 0 24"></polygon>
                                                <rect
                                                    id="Rectangle"
                                                    fill="#FFF"
                                                    opacity="1"
                                                    transform="translate(12.000000, 12.000000) rotate(-90.000000) translate(-12.000000, -12.000000) "
                                                    x="11"
                                                    y="5"
                                                    width="2"
                                                    height="14"
                                                    rx="1"
                                                ></rect>
                                                <path
                                                    d="M9.70710318,15.7071045 C9.31657888,16.0976288 8.68341391,16.0976288 8.29288961,15.7071045 C7.90236532,15.3165802 7.90236532,14.6834152 8.29288961,14.2928909 L14.2928896,8.29289093 C14.6714686,7.914312 15.281055,7.90106637 15.675721,8.26284357 L21.675721,13.7628436 C22.08284,14.136036 22.1103429,14.7686034 21.7371505,15.1757223 C21.3639581,15.5828413 20.7313908,15.6103443 20.3242718,15.2371519 L15.0300721,10.3841255 L9.70710318,15.7071045 Z"
                                                    id="Path-94"
                                                    fill="#FFF"
                                                    transform="translate(14.999999, 11.999997) scale(1, -1) rotate(90.000000) translate(-14.999999, -11.999997) "
                                                ></path>
                                            </g>
                                        </svg>
                                    </LocalizedLink>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <div className="position-relative mt-xl-10">
                <div className="shape shape-bottom shape-fluid-x svg-shim text-white">
                    <svg viewBox="0 0 2880 690" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0 345H1437.5H2880V0C1437.5 462.106 0 0 0 0V345Z" fill="#FFF" />
                    </svg>
                </div>
            </div>
            <section className="bg-white mb-10 mb-lg-15">
                <div className="container col-12 col-xl-10 mb-lg-15">
                    <div className="row align-items-stretch mb-lg-5 mb-xl-10">
                        <div className="col-12 col-xl-9 mt-xl-n10">
                            <svg className="ml-sm-n5 ml-n7" width="150px" height="150px" viewBox="0 0 24 24" version="1.1" xmlns="http://www.w3.org/2000/svg">
                                <g id="Stockholm-icons-/-General-/-Sad" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                                    <polygon id="“" fill="#FF6600" points="11 7 9 13 11 13 11 18 6 18 6 13 8 7"></polygon>
                                    <polygon id="Path" fill="#FF6600" opacity="0.6" points="19 7 17 13 19 13 19 18 14 18 14 13 16 7"></polygon>
                                </g>
                            </svg>
                            <h2 className="display-2 font-weight-bold text-secondary text-center text-md-left mt-n9 ml-sm-10 ml-lg-9 ml-xl-10">
                                {t('home.testimonials.heading')}
                            </h2>
                        </div>
                        <div className="col-12 mx-auto">
                            <div className="d-none d-md-block mt-xl-10 speech-bubble shadow shadow-lg">
                                <Testimonials />
                            </div>
                            <div className="d-md-none text-center testimonials-mobile">
                                <Testimonials />
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <section className="bg-white mt-5 mt-md-14 mt-lg-15 mb-10">
                <div className="container">
                    <div className="row">
                        <div className="col-12 col-md-9">
                            <h4 className="display-3 font-weight-bold text-secondary">{t('home.bottom.heading')}</h4>
                            <p className="lead text-gray-800">{t('home.bottom.subheading')}</p>
                        </div>
                        <div className="col-12 col-md-3">
                            <ScrollLink to="home" spy={true} smooth={true} duration={400}>
                                <button className="btn btn-primary btn-block shadow shadow-lg lift mt-5">{t('cta')}</button>
                            </ScrollLink>
                        </div>
                    </div>
                </div>
            </section>
        </Layout>
    );
};

export default WithIntl()(Home);
